import React from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { ArrowDownward, ArrowUpward, Warning } from '@material-ui/icons';
import { DataGrid, GridCellParams, GridColumns, GridSelectionModel } from '@material-ui/data-grid';
import { VolunteerProfile } from '../../../../../common/AvatarName';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { ActivityNameAndPriority } from '../ActivityInfo';
import { ApplicationResponsesCell } from '../ApplicationResponsesCell';
import {
  PENDING_REQUESTS_PAGE_SIZE,
  PendingRequestOrderBy,
  PendingRequestRow,
  getRequestDate,
  getVolunteerName,
  toApplicationResponses,
} from './pending-requests-types';
import '../../volunteer-database/simple-db.css';

interface Props {
  rows: PendingRequestRow[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  orderBy: PendingRequestOrderBy;
  orderAscending: boolean;
  onSort: (field: PendingRequestOrderBy) => void;
  dateLabel: string;
  selectionModel: GridSelectionModel;
  onSelectionModelChange: (selection: GridSelectionModel) => void;
  renderActions: (row: PendingRequestRow) => React.ReactNode;
}

export const PendingRequestsGrid = ({
  rows,
  total,
  page,
  onPageChange,
  loading,
  error,
  onRetry,
  orderBy,
  orderAscending,
  onSort,
  dateLabel,
  selectionModel,
  onSelectionModelChange,
  renderActions,
}: Props) => {
  const { isMobile, isSm } = useCampfireTheme();

  const sortableHeader = (field: PendingRequestOrderBy, label: string) => () => (
    <Box onClick={() => onSort(field)} style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }}>
      <Typography style={{ fontWeight: 600, fontSize: '14px' }}>{label}</Typography>
      {orderBy === field &&
        (orderAscending ? (
          <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
        ) : (
          <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
        ))}
    </Box>
  );

  const plainHeader = (label: string) => () => (
    <Typography style={{ fontWeight: 600, fontSize: '14px' }}>{label}</Typography>
  );

  const emptyHeader = () => <span />;

  const baseColumn = { flex: 1, disableColumnMenu: true, sortable: false, hideSortIcons: true };

  const columns: GridColumns = [
    {
      ...baseColumn,
      field: 'name',
      minWidth: 180,
      renderHeader: sortableHeader('name', 'Name'),
      renderCell: ({ row }: GridCellParams) => {
        const request = row as PendingRequestRow;
        return (
          <VolunteerProfile
            inTable
            noTrim
            profileId={request.profileId}
            userId={request.userId}
            preferredName={request.preferredName}
            lastName={request.lastName}
            avatarUrl={request.avatarUrl}
            name={getVolunteerName(request)}
            description={request.privilegeLevel}
          />
        );
      },
    },
    {
      ...baseColumn,
      field: 'programName',
      minWidth: 140,
      renderHeader: plainHeader('Program'),
      renderCell: ({ row }: GridCellParams) => row.programName,
    },
    {
      ...baseColumn,
      field: 'activityName',
      minWidth: 140,
      renderHeader: plainHeader('Activity'),
      renderCell: ({ row }: GridCellParams) => (
        <ActivityNameAndPriority name={row.activityName} priority={row.activityPriority} />
      ),
    },
    {
      ...baseColumn,
      field: 'date',
      minWidth: 120,
      renderHeader: sortableHeader('date', dateLabel),
      renderCell: ({ row }: GridCellParams) => getRequestDate(row as PendingRequestRow).toFormat('dd/MM/y'),
    },
    {
      ...baseColumn,
      field: 'email',
      minWidth: 180,
      renderHeader: sortableHeader('email', 'Email'),
      renderCell: ({ row }: GridCellParams) => row.email,
    },
    {
      ...baseColumn,
      field: 'contactNumber',
      minWidth: 120,
      renderHeader: plainHeader('Phone'),
      renderCell: ({ row }: GridCellParams) => row.contactNumber,
    },
    {
      ...baseColumn,
      field: 'answers',
      flex: undefined,
      width: 50,
      align: 'center',
      renderHeader: emptyHeader,
      renderCell: ({ row }: GridCellParams) => (
        <ApplicationResponsesCell answers={toApplicationResponses(row as PendingRequestRow)} />
      ),
    },
    {
      ...baseColumn,
      field: 'actions',
      flex: undefined,
      width: 50,
      align: 'center',
      renderHeader: emptyHeader,
      renderCell: ({ row }: GridCellParams) => renderActions(row as PendingRequestRow),
    },
  ];

  return (
    <Box
      style={{
        width: '98%',
        marginLeft: 'auto',
        marginRight: 'auto',
        overflowX: 'auto',
        flex: 1,
        minHeight: isMobile || isSm ? '400px' : '300px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <DataGrid
        columns={columns}
        rows={rows}
        loading={loading}
        pagination
        paginationMode='server'
        page={page}
        pageSize={PENDING_REQUESTS_PAGE_SIZE}
        rowsPerPageOptions={[PENDING_REQUESTS_PAGE_SIZE]}
        rowCount={total}
        onPageChange={onPageChange}
        checkboxSelection
        disableSelectionOnClick
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        rowHeight={70}
        headerHeight={30}
        style={{ fontSize: '14px', border: 'none', flex: 1, minHeight: 0 }}
        components={{
          NoRowsOverlay: () => (
            <Box
              height='100%'
              width='100%'
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              style={{ color: '#464646' }}
            >
              {error ? (
                <>
                  <Warning style={{ fontSize: '48px', color: '#ff9800', marginBottom: '16px' }} />
                  <Typography variant='body2'>{error}</Typography>
                  <Button
                    onClick={onRetry}
                    style={{ marginTop: '16px', textTransform: 'none' }}
                    variant='outlined'
                    size='small'
                  >
                    Try Again
                  </Button>
                </>
              ) : (
                <>
                  <span role='img' aria-label='No results' style={{ fontSize: '24px', marginBottom: '8px' }}>
                    😕
                  </span>
                  No results found
                </>
              )}
            </Box>
          ),
        }}
      />
    </Box>
  );
};
